import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Heart, Coffee, ExternalLink, Sparkles, CheckCircle2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import useTipper from "@/hooks/use-tipper";
import { clearTipper, saveTipper } from "@/lib/tipper";
import usePageMeta from "@/hooks/use-page-meta";
import { mutate } from "swr";
import { addSupporter } from "@/lib/api";
import { SupporterWall } from "@/components/SupporterWall";

const HANDLE = "tobiasdeamon";
const PRESETS = [
  { amount: 3, label: "Buy a coffee", icon: Coffee },
  { amount: 5, label: "Fuel a benchmark", icon: Sparkles },
  { amount: 10, label: "Sponsor a session", icon: Heart },
  { amount: 25, label: "Season pass", icon: Heart },
];

export default function Support() {
  const [custom, setCustom] = useState("");
  const [claimOpen, setClaimOpen] = useState(false);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const tipper = useTipper();
  usePageMeta({
    title: "Support the tobiasdeamon Video Archive",
    description:
      "Support the tobiasdeamon Vault with a PayPal tip. Every coffee funds the next benchmark, new licenses, and rig upgrades. Ad-free view for supporters.",
  });

  const link = (amount) => {
    if (!amount) return `https://paypal.me/${HANDLE}`;
    return `https://paypal.me/${HANDLE}/${amount}EUR`;
  };

  const handleClaim = async () => {
    saveTipper({ name, note });
    if (name.trim()) {
      try {
        await addSupporter(name.trim().slice(0, 40));
        mutate("/supporters");
      } catch {
        /* wall is optional */
      }
    }
    toast.success("Ads are off. Thank you for supporting the vault ❤");
    setClaimOpen(false);
    setName("");
    setNote("");
  };

  const handleUnclaim = () => {
    clearTipper();
    toast.message("Supporter mode turned off. Ads will resume.");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
      <div className="mono-label">Support</div>
      <h1 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#f4f1ea] mt-3">
        Keep the vault running.
      </h1>
      <p className="mt-6 text-white/60 text-lg max-w-2xl leading-relaxed">
        This archive is a labor of love — thousands of gameplay hours, rig upgrades, new game
        licenses. If any of it saved you time, taught you something, or just kept you company,
        drop a tip. Every coffee helps the next benchmark.
      </p>

      {tipper && (
        <div
          data-testid="supporter-banner"
          className="mt-10 rounded-2xl border border-[#d95d39]/40 bg-[#d95d39]/[0.08] p-6 flex flex-wrap items-center justify-between gap-4"
        >
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-[#d95d39]" />
            <div>
              <div className="mono-label text-[#d95d39]">Supporter · ads off</div>
              <div className="text-[#f4f1ea] font-serif-editorial text-2xl">
                Thank you{tipper.name ? `, ${tipper.name}` : ""} ❤
              </div>
              {tipper.note && (
                <div className="text-white/60 text-sm mt-1 max-w-xl">
                  "{tipper.note}"
                </div>
              )}
            </div>
          </div>
          <Button
            onClick={handleUnclaim}
            variant="outline"
            data-testid="supporter-clear-btn"
            className="rounded-full bg-transparent border-white/20 text-white/70 hover:border-[#d95d39] hover:text-[#d95d39] font-mono-tech text-[11px] uppercase tracking-[0.22em]"
          >
            <X className="w-3.5 h-3.5 mr-2" /> Turn off
          </Button>
        </div>
      )}

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="paypal-preset-grid">
        {PRESETS.map((p) => (
          <a
            key={p.amount}
            href={link(p.amount)}
            target="_blank"
            rel="noreferrer"
            data-testid={`paypal-preset-${p.amount}`}
            className="group hover-lift block rounded-2xl border border-white/10 bg-[#12141d] p-6"
          >
            <p.icon className="w-6 h-6 text-[#d95d39]" />
            <div className="mt-6 font-serif-editorial text-4xl text-[#f4f1ea]">
              €{p.amount}
            </div>
            <div className="mono-label text-white/50 mt-2">{p.label}</div>
            <div className="mt-4 inline-flex items-center gap-1 text-[#d95d39] font-mono-tech text-[11px] uppercase tracking-[0.22em] opacity-0 group-hover:opacity-100 transition-opacity">
              Continue on PayPal <ExternalLink className="w-3 h-3" />
            </div>
          </a>
        ))}
      </div>

      <div className="mt-10 rounded-2xl border border-white/10 bg-[#12141d] p-6">
        <div className="mono-label">Custom amount</div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 font-serif-editorial text-2xl text-white/50">
              €
            </span>
            <Input
              data-testid="paypal-custom-input"
              type="number"
              min="1"
              inputMode="decimal"
              value={custom}
              onChange={(e) => setCustom(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder="Amount"
              className="pl-8 h-14 text-xl bg-[#0c0e15] border-white/10 focus-visible:ring-[#d95d39] text-[#f4f1ea]"
            />
          </div>
          <a
            href={link(custom || "")}
            target={custom ? "_blank" : undefined}
            rel="noreferrer"
            data-testid="paypal-custom-submit"
            className={`inline-flex items-center gap-2 px-6 py-4 rounded-full font-mono-tech text-[11px] uppercase tracking-[0.22em] transition-colors ${
              custom
                ? "bg-[#d95d39] hover:bg-[#c14e2e] text-white"
                : "bg-white/[0.04] text-white/40 pointer-events-none"
            }`}
          >
            <Heart className="w-4 h-4" />
            {custom ? `Send €${custom} via PayPal` : "Enter an amount"}
          </a>
        </div>
      </div>

      {!tipper && (
        <div className="mt-8 rounded-2xl border border-dashed border-white/15 bg-[#0c0e15] p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="mono-label">Already tipped?</div>
            <div className="mt-2 text-[#f4f1ea] font-serif-editorial text-2xl">
              Claim your ad-free view.
            </div>
            <p className="text-white/50 mt-1 text-sm max-w-xl">
              We can't verify PayPal.me tips automatically. Tap the button and we'll switch
              ads off on this device. Honor system — thank you for being kind.
            </p>
          </div>
          <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="claim-supporter-btn"
                className="rounded-full bg-[#d95d39] hover:bg-[#c14e2e] text-white font-mono-tech text-[11px] uppercase tracking-[0.22em] px-5"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" /> I tipped
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#12141d] border-white/10 text-[#f4f1ea] max-w-md">
              <DialogHeader>
                <DialogTitle className="font-serif-editorial text-2xl">
                  Claim ad-free view
                </DialogTitle>
                <DialogDescription className="text-white/60 text-sm">
                  Optional — your name goes on the public supporter wall below. The note stays on this device only.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 pt-2">
                <Input
                  data-testid="claim-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name for the wall (optional)"
                  maxLength={40}
                  className="bg-[#0c0e15] border-white/10 focus-visible:ring-[#d95d39]"
                />
                <Textarea
                  data-testid="claim-note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="A short note (optional)"
                  maxLength={240}
                  rows={3}
                  className="bg-[#0c0e15] border-white/10 focus-visible:ring-[#d95d39]"
                />
              </div>
              <DialogFooter>
                <Button
                  onClick={handleClaim}
                  data-testid="claim-submit-btn"
                  className="rounded-full bg-[#d95d39] hover:bg-[#c14e2e] text-white font-mono-tech text-[11px] uppercase tracking-[0.22em]"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Turn ads off
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <div className="mt-10 flex flex-wrap items-center gap-3">
        <a
          href={`https://paypal.me/${HANDLE}`}
          target="_blank"
          rel="noreferrer"
          data-testid="paypal-open-link"
          className="mono-label text-white/60 hover:text-[#d95d39] inline-flex items-center gap-2"
        >
          Or open paypal.me/{HANDLE} directly <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>

      <SupporterWall />

      <p className="mt-16 text-white/40 text-sm max-w-xl">
        Tips are one-off, non-refundable, and never grant anything on the site — the archive
        will always be free to browse. Thank you.
      </p>
    </div>
  );
}
